/**
 * Phase 7 — Shared media-query primitives
 *
 * Single source of truth for the device/capability media queries the
 * interaction layer depends on. Replaces the duplicated module-level
 * `matchMedia(...).matches` reads in useTilt / useTouchTilt with one
 * SSR-safe, lazily-evaluated, reactive surface.
 *
 * Why a module:
 *  - DRY: the same 3 queries (hover, coarse pointer, reduced motion)
 *    were redefined in every hook that needed them.
 *  - SSR-safe: `typeof window` guard centralized once.
 *  - Reactive: `useMediaQuery()` listens for changes (e.g. user toggles
 *    "Reduce motion" in OS settings) and re-renders consumers.
 *  - Static reads stay available for hooks that intentionally want a
 *    snapshot at mount (e.g. inside a non-React listener).
 */

import { useEffect, useState } from "react";

export const MQ = {
  hoverFine:     "(hover: hover) and (pointer: fine)",
  coarseTouch:   "(hover: none) and (pointer: coarse)",
  reducedMotion: "(prefers-reduced-motion: reduce)",
} as const;

export type MediaQueryToken = keyof typeof MQ;

/** SSR-safe synchronous read — returns false during SSR or when window is missing. */
export function matchesQuery(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}

export const supportsHover    = () => matchesQuery(MQ.hoverFine);
export const isCoarsePointer  = () => matchesQuery(MQ.coarseTouch);
export const prefersReduced   = () => matchesQuery(MQ.reducedMotion);

/** Reactive hook — re-renders the consumer when the query result changes. */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => matchesQuery(query));
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mql = window.matchMedia(query);
    const handler = () => setMatches(mql.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}
